import { get, post, put, del } from '@/api/lib/amplify-wrapper'
import { createImpersonatedApiHeaders } from '@/lib/services/api-utils'
import type {
  CalendarEvent,
  CreateEventPayload,
  UpdateEventPayload,
  GetCalendarEventsParams
} from '@/types/calendar'
import type { ConflictCheckRequest, ConflictResult } from '@/lib/calendar/conflict-detection'

const API_NAME = 'workforce'

/**
 * Helper function to serialize data for API requests
 */
const serializeData = (data: any) => {
  return JSON.parse(JSON.stringify(data, (_key, value) => {
    // Convert Date objects to ISO strings
    if (value instanceof Date) {
      return value.toISOString()
    }
    return value
  }))
}

/**
 * Fetch team calendar events for the current user
 * Returns events for the authenticated user's team members within the specified date range
 */
export const getMyTeamEvents = async (
  params: GetCalendarEventsParams
): Promise<CalendarEvent[]> => {
  const queryParams = new URLSearchParams()
  queryParams.append('startDate', params.startDate)
  queryParams.append('endDate', params.endDate)

  if (params.filters && params.filters.length > 0) {
    queryParams.append('types', params.filters.join(','))
  }

  const path = `/workforce/calendar/me/team-events?${queryParams.toString()}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()

  // Backend returns array of events directly
  return data as unknown as CalendarEvent[]
}

/**
 * Create a new calendar event
 */
export const createEvent = async (
  payload: CreateEventPayload
): Promise<CalendarEvent> => {
  const path = '/workforce/calendar/events'

  const restOperation = post({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(payload),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as CalendarEvent
}

/**
 * Update an existing calendar event
 */
export const updateEvent = async (
  eventId: string,
  payload: UpdateEventPayload
): Promise<CalendarEvent> => {
  const path = `/workforce/calendar/events/${eventId}`

  const restOperation = put({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(payload),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as CalendarEvent
}

/**
 * Delete a calendar event
 */
export const deleteEvent = async (eventId: string): Promise<void> => {
  const path = `/workforce/calendar/events/${eventId}`

  const restOperation = del({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  await restOperation.response
}

/**
 * Check for calendar conflicts
 * Detects scheduling conflicts for given attendees and time range
 */
export const checkConflicts = async (
  payload: ConflictCheckRequest
): Promise<ConflictResult[]> => {
  const path = '/workforce/calendar/conflicts'

  const restOperation = post({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(payload),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as ConflictResult[]
}

/**
 * Calendar API client object
 */
export const calendarApi = {
  getMyTeamEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  checkConflicts
}
